import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { track } from "@/lib/api";
import { toast } from "sonner";
import {
  HandsPraying,
  BookOpen,
  Heart,
  Compass,
  BookmarkSimple,
  SpinnerGap,
  Microphone,
  MicrophoneSlash,
  ShieldCheck,
  ArrowLeft,
  Play,
  Pause,
  SpeakerHigh,
  SpeakerSlash,
  Stop,
  X,
  Info,
} from "@phosphor-icons/react";

const BOT_ICONS = { prayerbot: HandsPraying, counselor: BookOpen, companion: Heart, guide: Compass };
const BOT_LABELS = { prayerbot: "Prayer", counselor: "Bible", companion: "Faith", guide: "Guide" };

// Strip markdown so TTS reads natural prose — mirrors backend _clean_for_tts
// so voice is correct even if the server hasn't been redeployed yet.
const OPENER_RE = /^\s*(?:absolutely[.,!]*|of\s+course[.,!]*|certainly[.,!]*|sure[.,!]*|sure\s+thing[.,!]*|great\s+question[.,!]*|wonderful\s+question[.,!]*|thank\s+you\s+for[^.]*?[.,!]+|what\s+a\s+beautiful\s+(?:question|request)[.,!]*|lifting\s+(?:up|your)[^.]*?[.!]+|here\s+is\s+(?:a|your)[^.:\n]*?[.:!]+|here\s*['’]?s\s+(?:a|your)[^.:\n]*?[.:!]+|let\s*['’]?s\s+pray[^.:\n]*?[.:!]+|may\s+this\s+prayer[^.:\n]*?[.:!]+)\s*/i;

const cleanForTTS = (text) => {
  if (!text) return "";
  let t = text;

  // 1. Peel generic conversational openers.
  let peeled = t.replace(/^\s+/, "");
  for (let i = 0; i < 3; i++) {
    const m = peeled.match(OPENER_RE);
    if (!m) break;
    peeled = peeled.slice(m[0].length).replace(/^\s+/, "");
  }
  if (peeled && peeled !== t.replace(/^\s+/, "")) t = peeled;

  // 2. If a divider / bold header is early, jump to it.
  const introMatch = t.match(/^(?:\s*---\s*$|\s*\*{2}[^*\n]+?\*{2}\s*:?\s*$|\s*#{1,6}\s+\S[^\n]*$)/m);
  if (introMatch && introMatch.index > 0) {
    const cut = introMatch.index;
    const preamble = t.slice(0, cut).trim();
    if (cut <= t.length * 0.4 && preamble.length <= 240 && (preamble.match(/\.\s/g) || []).length <= 2) {
      t = t.slice(cut).replace(/^\s+/, "");
    }
  }

  t = t.replace(/```[^\n]*\n([\s\S]*?)\n```/g, "$1");        // fenced code
  t = t.replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1");              // images
  t = t.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");               // links
  t = t.replace(/\*\*\*([\s\S]+?)\*\*\*/g, "$1");              // ***bold italic***
  t = t.replace(/\*\*([\s\S]+?)\*\*/g, "$1");                  // **bold**
  t = t.replace(/__([^_\n]+?)__/g, "$1");                      // __bold__
  t = t.replace(/(?<!\*)\*(?!\*)([^*\n]+?)\*(?!\*)/g, "$1");   // *italic*
  t = t.replace(/(?<!_)_(?!_)([^_\n]+?)_(?!_)/g, "$1");        // _italic_
  t = t.replace(/^\s*#{1,6}\s+/gm, "");                         // # headers
  t = t.replace(/^\s*(?:[-*_]\s?){3,}\s*$/gm, "");              // --- *** ___
  t = t.replace(/^\s*>\s?/gm, "");                              // > quotes
  t = t.replace(/`([^`]+)`/g, "$1");                            // `code`
  t = t.replace(/^\s*[-*+]\s+/gm, "");                          // - bullets
  t = t.replace(/^\s*\d+\.\s+/gm, "");                          // 1. bullets
  t = t.replace(/\n{3,}/g, "\n\n");
  // Strip AI-disclaimer tail.
  t = t.replace(/\s*\(?\s*\*?\s*\(?\s*(?:AI[- ]?generated[^)]*|This (?:prayer|response) (?:is|was) AI[- ]?generated[^)]*|For deeper care[^)]*)\)?\s*\*?\s*\)?\s*$/i, "");
  return t.trim();
};

export default function ConnectPage() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const [activeBotId, setActiveBotId] = useState(botId || "prayerbot");
  const [bots, setBots] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Voice state
  const [voiceConfig, setVoiceConfig] = useState({ enabled: false, default_voice_id: null, voices: [] });
  const [voiceConfigStatus, setVoiceConfigStatus] = useState("loading"); // loading | ok | disabled | error
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("vg_voice_on") !== "false");
  const [autoplay, setAutoplay] = useState(() => localStorage.getItem("vg_voice_autoplay") !== "false");
  const [voiceId, setVoiceId] = useState(() => localStorage.getItem("vg_voice_id") || "");
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null); // which message's TTS is being prepared
  const [voiceStage, setVoiceStage] = useState(null); // recording | transcribing | thinking | synthesizing | null
  const [playProgress, setPlayProgress] = useState(0); // 0..1 for currently playing message
  const [browserSupport, setBrowserSupport] = useState({ checked: false, ok: true, reason: "" });
  const [micPermission, setMicPermission] = useState("unknown"); // unknown | granted | denied | prompt

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const objectUrlRef = useRef(null);
  const cancelledRef = useRef(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { api.get("/chat/bots").then((r) => setBots(r.data.bots)).catch(() => {}); }, []);
  useEffect(() => {
    // Capability detection — runs once.
    const secure = typeof window !== "undefined" && window.isSecureContext;
    const hasMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasRecorder = typeof MediaRecorder !== "undefined";
    let ok = true; let reason = "";
    if (!secure) { ok = false; reason = "Voice requires HTTPS or localhost."; }
    else if (!hasMedia) { ok = false; reason = "This browser does not expose a microphone API."; }
    else if (!hasRecorder) { ok = false; reason = "This browser does not support MediaRecorder."; }
    setBrowserSupport({ checked: true, ok, reason });
    // Best-effort permission query (Chromium/FF supports this; Safari silently fails).
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "microphone" })
        .then((p) => { setMicPermission(p.state); p.onchange = () => setMicPermission(p.state); })
        .catch(() => { /* not supported — fine */ });
    }
  }, []);
  const loadVoiceConfig = () => {
    setVoiceConfigStatus("loading");
    api.get("/voice/config")
      .then((r) => {
        setVoiceConfig(r.data);
        if (!voiceId && r.data.default_voice_id) setVoiceId(r.data.default_voice_id);
        setVoiceConfigStatus(r.data.enabled ? "ok" : "disabled");
      })
      .catch(() => setVoiceConfigStatus("error"));
  };
  useEffect(() => { loadVoiceConfig(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (botId && botId !== activeBotId) setActiveBotId(botId); }, [botId]);
  useEffect(() => {
    setHistoryLoading(true); setMessages([]);
    api.get(`/chat/history/${activeBotId}`).then((r) => setMessages(r.data.messages)).catch(() => {}).finally(() => setHistoryLoading(false));
  }, [activeBotId]);
  // Smart auto-scroll: only snap to bottom if user is already near the bottom.
  // Prevents fighting the user's scroll while listening to a long reply.
  const messagesContainerRef = useRef(null);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 140) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading, processing]);

  useEffect(() => { localStorage.setItem("vg_voice_on", String(voiceOn)); }, [voiceOn]);
  useEffect(() => { localStorage.setItem("vg_voice_autoplay", String(autoplay)); }, [autoplay]);
  useEffect(() => { if (voiceId) localStorage.setItem("vg_voice_id", voiceId); }, [voiceId]);

  // Clean up audio + blob URL on unmount
  useEffect(() => {
    return () => {
      try { audioRef.current?.pause(); } catch {/* ignore */}
      if (objectUrlRef.current) {
        try { URL.revokeObjectURL(objectUrlRef.current); } catch {/* ignore */}
      }
    };
  }, []);

  const activeBot = bots.find((b) => b.id === activeBotId);
  const BotIcon = BOT_ICONS[activeBotId] || Compass;

  const switchBot = (id) => { setActiveBotId(id); navigate(`/connect/${id}`, { replace: true }); };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg, created_at: new Date().toISOString() }]);
    setLoading(true);
    track("ai_guidance_message", { bot: activeBotId, mode: "text" });
    const token = localStorage.getItem("vg_token");
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      const response = await fetch(`${API_URL}/api/chat/stream`, {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ bot_id: activeBotId, message: msg }),
      });
      if (!response.ok) throw new Error("Stream failed");
      setMessages((prev) => [...prev, { role: "assistant", content: "", created_at: new Date().toISOString(), streaming: true }]);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === "chunk") {
                fullContent += data.content;
                setMessages((prev) => { const u = [...prev]; u[u.length - 1] = { ...u[u.length - 1], content: fullContent }; return u; });
              } else if (data.type === "done") {
                setMessages((prev) => { const u = [...prev]; u[u.length - 1] = { ...u[u.length - 1], streaming: false, id: data.id }; return u; });
              } else if (data.type === "error") throw new Error(data.message);
            } catch (e) { if (e.message && !e.message.includes("JSON")) throw e; }
          }
        }
      }
    } catch { toast.error("Connection interrupted. Please try again."); setMessages((prev) => prev[prev.length - 1]?.streaming ? prev.slice(0, -1) : prev); }
    finally { setLoading(false); inputRef.current?.focus(); }
  };

  const saveToJournal = async (msg) => {
    try { await api.post("/journal", { type: "prayer", title: `From ${activeBot?.name || "AI"}`, content: msg.content, bot_id: activeBotId }); toast.success("Saved to journal"); } catch { toast.error("Failed to save"); }
  };

  // -------- Voice recording --------
  const pickMimeType = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg", "audio/ogg"];
    for (const t of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
    }
    return "";
  };

  const startRecording = async () => {
    if (recording || processing || loading) return;
    // Always actionable: don't silently no-op. Give the user a precise reason.
    if (browserSupport.checked && !browserSupport.ok) {
      const msg = !window.isSecureContext
        ? "Microphone access requires HTTPS or localhost."
        : !(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
          ? "Voice input is not supported in this browser. Try Chrome, Edge, Firefox, or Safari."
          : "This browser does not support MediaRecorder.";
      toast.error(msg, { duration: 6000 });
      return;
    }
    if (voiceConfigStatus === "disabled") {
      toast.error("Voice services are temporarily unavailable. Text chat still works.", { duration: 5000 });
      return;
    }
    if (voiceConfigStatus === "error") {
      toast.error("Couldn't reach the voice service. Checking again…", { duration: 4000 });
      loadVoiceConfig();
      return;
    }
    if (voiceConfigStatus === "loading") {
      toast.info("Getting voice ready… one moment.");
      return;
    }
    unlockAudio();  // synchronous within user gesture — enables later autoplay
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission("granted");
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      cancelledRef.current = false;
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (cancelledRef.current) { setRecording(false); return; }
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        setRecording(false);
        if (blob.size < 1200) { toast.error("Recording too short — try again"); return; }
        await sendVoice(blob, mr.mimeType || "audio/webm");
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setMicPermission("denied");
        toast.error(
          "Microphone permission is blocked in your browser. Click the lock/site-info icon in the address bar → Site settings → Microphone → Allow, then reload.",
          { duration: 8000 }
        );
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        toast.error("No microphone detected on this device.", { duration: 5000 });
      } else if (name === "NotReadableError") {
        toast.error("Microphone is being used by another app. Close it and try again.", { duration: 5000 });
      } else {
        toast.error(`Could not start microphone: ${e?.message || "unknown error"}`, { duration: 5000 });
      }
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    try { mediaRecorderRef.current?.stop(); } catch {/* ignore */}
  };

  const cancelRecording = () => {
    cancelledRef.current = true;
    try { mediaRecorderRef.current?.stop(); } catch {/* ignore */}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {/* ignore */}
    setRecording(false);
  };

  const sendVoice = async (blob, mimeType) => {
    setProcessing(true);
    setVoiceStage("transcribing");
    const tempId = `voice-${Date.now()}`;
    setMessages((prev) => [...prev, { role: "user", content: "Transcribing your voice…", created_at: new Date().toISOString(), input_mode: "voice", tempId }]);
    // Progress the stage label even though the server does it all in one call —
    // so the user sees "Transcribing → Thinking → Generating audio" rather than a frozen bar.
    const stageTimers = [
      setTimeout(() => setVoiceStage("thinking"), 1400),
      setTimeout(() => setVoiceStage("synthesizing"), 3800),
    ];
    try {
      const form = new FormData();
      const ext = mimeType.includes("mp4") ? "mp4" : mimeType.includes("mpeg") ? "mp3" : mimeType.includes("ogg") ? "ogg" : "webm";
      form.append("audio", blob, `recording.${ext}`);
      form.append("bot_id", activeBotId);
      if (voiceOn && voiceId) form.append("voice_id", voiceId);

      const token = localStorage.getItem("vg_token");
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/voice/chat`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Voice chat failed");
      }
      const data = await res.json();
      setMessages((prev) => {
        const updated = prev.filter((m) => m.tempId !== tempId);
        updated.push({
          role: "user", content: data.transcript,
          created_at: data.created_at, input_mode: "voice", id: data.user_id,
        });
        updated.push({
          role: "assistant", content: data.assistant_text,
          created_at: data.created_at, id: data.assistant_id,
          audio_base64: data.audio_base64, mime_type: data.mime_type, voice_id: data.voice_id,
          _clean_version: "v2",
        });
        return updated;
      });
      if (voiceOn && autoplay && data.audio_base64) {
        playAudio(data.assistant_id, data.audio_base64, data.mime_type);
      }
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.tempId !== tempId));
      toast.error(e.message || "Voice chat failed");
    } finally {
      stageTimers.forEach(clearTimeout);
      setVoiceStage(null);
      setProcessing(false);
    }
  };

  // Tiny silent WAV (< 1KB). Used to satisfy Chrome's "first play must be
  // inside a user gesture" rule so later async plays succeed.
  const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

  const getAudioEl = () => {
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = "auto";
      el.onended = () => { setPlayingId(null); setPlayProgress(0); };
      el.onerror = () => { setPlayingId(null); setPlayProgress(0); };
      el.ontimeupdate = () => {
        if (el.duration && isFinite(el.duration)) {
          setPlayProgress(el.currentTime / el.duration);
        }
      };
      audioRef.current = el;
    }
    return audioRef.current;
  };

  // Call this SYNCHRONOUSLY inside a click/tap handler.
  const unlockAudio = () => {
    if (audioUnlockedRef.current) return;
    try {
      const el = getAudioEl();
      el.src = SILENT_WAV;
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => { el.pause(); el.currentTime = 0; audioUnlockedRef.current = true; })
          .catch(() => { /* user agent may reject — fine, we'll still try later */ });
      } else {
        audioUnlockedRef.current = true;
      }
    } catch { /* ignore */ }
  };

  const playAudio = (id, audio_base64, mime_type) => {
    if (!audio_base64) return;
    try {
      const el = getAudioEl();
      // Revoke previous blob URL to avoid memory leaks
      if (objectUrlRef.current) {
        try { URL.revokeObjectURL(objectUrlRef.current); } catch {/* ignore */}
        objectUrlRef.current = null;
      }
      const bytes = Uint8Array.from(atob(audio_base64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: mime_type || "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      el.src = url;
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => setPlayingId(id))
          .catch((err) => {
            setPlayingId(null);
            // Autoplay policy — tell the user they need to tap Replay.
            if (err?.name === "NotAllowedError") {
              toast.info("Tap Replay to hear the reply — autoplay was blocked by your browser.");
            }
          });
      }
    } catch { setPlayingId(null); }
  };

  const stopAudio = () => {
    try { audioRef.current?.pause(); } catch {/* ignore */}
    setPlayingId(null);
    setPlayProgress(0);
  };

  const speakAssistant = async (msg) => {
    if (voiceConfigStatus !== "ok") { toast.error("Voice not available"); return; }
    const id = msg.id || `msg-${msg.content?.slice(0,16)}`;
    // Spam-click guard: if we're already generating this or playing it, ignore.
    if (generatingId === id) return;
    unlockAudio();  // within click gesture
    if (msg.audio_base64 && msg._clean_version === "v2") {
      playAudio(id, msg.audio_base64, msg.mime_type);
      return;
    }
    setGeneratingId(id);
    try {
      const cleaned = cleanForTTS(msg.content);
      const res = await api.post("/voice/speak", { text: cleaned, voice_id: voiceId || undefined });
      setMessages((prev) => prev.map((m) =>
        m.id === msg.id
          ? { ...m, audio_base64: res.data.audio_base64, mime_type: res.data.mime_type, voice_id: res.data.voice_id, _clean_version: "v2" }
          : m
      ));
      playAudio(id, res.data.audio_base64, res.data.mime_type);
    } catch {
      toast.error("Couldn't speak that reply");
    } finally {
      setGeneratingId(null);
    }
  };

  const isEmpty = messages.length === 0 && !historyLoading;

  return (
    <div className="flex flex-col h-screen" data-testid="connect-page">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <Link to="/home" className="text-[#8B7355] p-1"><ArrowLeft size={20} /></Link>
          <h1 className="font-heading text-lg text-[#2D1B0E]">Connect</h1>
          <button
            onClick={() => setVoiceOn((v) => !v)}
            data-testid="voice-toggle-btn"
            title={voiceOn ? "Voice replies on" : "Voice replies muted"}
            className="text-[#8B7355] p-1 hover:text-[#C4A265] transition-colors"
          >
            {voiceOn ? <SpeakerHigh size={20} weight="duotone" /> : <SpeakerSlash size={20} weight="duotone" />}
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {bots.map((bot) => {
            const Icon = BOT_ICONS[bot.id] || Compass;
            const active = bot.id === activeBotId;
            return (
              <button key={bot.id} onClick={() => switchBot(bot.id)} data-testid={`bot-tab-${bot.id}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-body whitespace-nowrap transition-all ${active ? "glass-card-strong text-[#5C4033] font-medium" : "text-[#8B7355] hover:bg-white/30"}`}>
                <Icon size={16} weight="duotone" />
                {BOT_LABELS[bot.id] || bot.name.replace("AI ", "")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-2" ref={messagesContainerRef}>
        <div className="max-w-2xl mx-auto space-y-3">
          {isEmpty && activeBot && (
            <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in relative">
              <div className="spiritual-orb-connect w-48 h-48 animate-orb" style={{ top: "10%", left: "50%", transform: "translateX(-50%)" }} />
              <div className="relative z-10">
                <BotIcon size={36} weight="duotone" className="text-[#C4A265] mx-auto mb-4" />
                <h2 className="font-heading text-2xl text-[#2D1B0E] mb-2">{activeBot.name}</h2>
                <p className="text-[#8B7355] font-body text-sm max-w-sm mb-8">{activeBot.welcome}</p>
                <div className="flex flex-wrap gap-2 justify-center max-w-md">
                  {activeBot.prompts?.map((p, i) => (
                    <button key={i} onClick={() => sendMessage(p)} data-testid={`suggested-prompt-${i}`}
                      className="px-4 py-2 rounded-full glass-card text-[#5C4033] text-sm font-body hover:bg-white/60 transition-all">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-8 text-[#8B7355] text-[10px] font-body">
                <ShieldCheck size={12} weight="duotone" className="text-[#C4A265]" />
                Grounded in Scripture and trusted commentary
              </div>
            </div>
          )}

          {historyLoading && <div className="flex justify-center py-16"><SpinnerGap size={24} className="text-[#C4A265] animate-spin" /></div>}

          {messages.map((msg, i) => (
            <div key={i} className={`chat-message flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] sm:max-w-[70%] p-4 rounded-2xl ${
                msg.role === "user" ? "glass-card-strong text-[#2D1B0E] rounded-tr-sm" : "bg-white/60 border border-[#E8DFD3]/50 text-[#2D1B0E] rounded-tl-sm"
              }`} data-testid={`${msg.role}-message-${i}`}>
                {msg.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <BotIcon size={12} weight="duotone" className="text-[#C4A265]" />
                    <span className="text-[#C4A265] text-[10px] font-body font-medium">{activeBot?.name}</span>
                    <span className="text-[#B8A088] text-[9px] font-body">AI-assisted</span>
                  </div>
                )}
                {msg.role === "user" && msg.input_mode === "voice" && (
                  <div className="flex items-center gap-1 mb-1 text-[#C4A265] text-[9px] font-body">
                    <Microphone size={10} weight="fill" /> voice
                  </div>
                )}
                <div className="font-body text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</div>
                {msg.role === "assistant" && !msg.streaming && (
                  <div className="mt-3 space-y-2">
                    {voiceConfigStatus === "ok" && (() => {
                      const thisId = msg.id || `msg-${msg.content?.slice(0,16)}`;
                      if (generatingId === thisId) {
                        return (
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAF5EC] border border-[#C4A265]/40 text-[#5C4033] font-body text-xs font-medium" data-testid={`generating-audio-${i}`}>
                            <SpinnerGap size={14} className="text-[#C4A265] animate-spin" />
                            Generating audio…
                          </div>
                        );
                      }
                      if (playingId === thisId) {
                        return (
                          <div className="space-y-1.5" data-testid={`audio-player-${i}`}>
                            <button
                              onClick={stopAudio}
                              data-testid={`stop-audio-${i}`}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C4A265] text-white font-body text-xs font-medium shadow-sm hover:bg-[#B8965C] transition-all"
                            >
                              <span className="relative flex items-center justify-center w-2 h-2">
                                <span className="absolute w-2 h-2 rounded-full bg-white/80 animate-ping" />
                                <span className="w-1.5 h-1.5 rounded-full bg-white" />
                              </span>
                              Playing · Tap to stop
                            </button>
                            <div className="h-1 bg-[#EDE6DC] rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#C4A265] transition-[width] duration-100"
                                style={{ width: `${Math.round(playProgress * 100)}%` }}
                              />
                            </div>
                          </div>
                        );
                      }
                      return (
                        <button
                          onClick={() => speakAssistant(msg)}
                          disabled={!!generatingId}
                          data-testid={`play-audio-${i}`}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#C4A265]/40 text-[#5C4033] font-body text-xs font-medium hover:bg-[#FAF5EC] hover:border-[#C4A265] transition-all disabled:opacity-50 disabled:cursor-wait"
                        >
                          <Play size={14} weight="fill" className="text-[#C4A265]" />
                          {msg.audio_base64 && msg._clean_version === "v2" ? "Replay voice" : "Listen to voice"}
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => saveToJournal(msg)}
                      data-testid={`save-msg-${i}`}
                      className="flex items-center gap-1 text-[#B8A088] hover:text-[#C4A265] text-[11px] font-body transition-colors"
                    >
                      <BookmarkSimple size={12} weight="duotone" /> Save to journal
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {(loading || processing) && messages.length > 0 && messages[messages.length - 1].role === "user" && (
            <div className="flex justify-start chat-message">
              <div className="bg-white/60 border border-[#E8DFD3]/50 rounded-2xl rounded-tl-sm p-4">
                {processing && voiceStage === "synthesizing" ? (
                  <div className="flex items-center gap-2 font-body text-xs text-[#8B7355]">
                    <SpinnerGap size={14} className="text-[#C4A265] animate-spin" />
                    Generating audio reply…
                  </div>
                ) : processing && voiceStage === "thinking" ? (
                  <div className="flex items-center gap-2 font-body text-xs text-[#8B7355]">
                    <SpinnerGap size={14} className="text-[#C4A265] animate-spin" />
                    Praying through your words…
                  </div>
                ) : (
                  <div className="typing-indicator"><span></span><span></span><span></span></div>
                )}
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="flex-shrink-0 px-4 py-3">
        <div className="max-w-2xl mx-auto">
          {recording && (
            <div className="glass-card-strong p-3 mb-2 flex items-center gap-3" data-testid="recording-bar">
              <span className="relative flex items-center justify-center">
                <span className="absolute w-3 h-3 rounded-full bg-red-500 animate-ping" />
                <span className="w-3 h-3 rounded-full bg-red-500" />
              </span>
              <span className="flex-1 font-body text-sm text-[#5C4033]">Listening… tap stop when done</span>
              <button onClick={cancelRecording} data-testid="cancel-record-btn" className="text-[#8B7355] hover:text-red-500 p-1">
                <X size={18} />
              </button>
              <button onClick={stopRecording} data-testid="stop-record-btn"
                className="px-4 py-2 rounded-full glass-card text-[#5C4033] font-body text-sm font-medium hover:bg-white/60 transition-all flex items-center gap-1">
                <Stop size={14} weight="fill" /> Stop
              </button>
            </div>
          )}
          {processing && (
            <div className="glass-card p-3 mb-2 flex items-center gap-3" data-testid="processing-bar">
              <SpinnerGap size={16} className="text-[#C4A265] animate-spin" />
              <span className="font-body text-sm text-[#5C4033]">
                {voiceStage === "transcribing" && "Transcribing your voice…"}
                {voiceStage === "thinking" && "Praying through your words…"}
                {voiceStage === "synthesizing" && "Generating audio reply…"}
                {!voiceStage && "Working…"}
              </span>
              <div className="ml-auto flex items-center gap-1">
                {["transcribing", "thinking", "synthesizing"].map((s, idx) => {
                  const active = voiceStage === s;
                  const passed = ["transcribing", "thinking", "synthesizing"].indexOf(voiceStage) > idx;
                  return (
                    <span
                      key={s}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${
                        active ? "bg-[#C4A265] w-2.5" : passed ? "bg-[#C4A265]/60" : "bg-[#D4C9BA]"
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {(() => {
            // Only show banner for conditions the user cannot fix by tapping the mic.
            // Permission-denied and loading/error states are handled via toasts on click.
            if (browserSupport.checked && !browserSupport.ok) {
              const specific = !window.isSecureContext
                ? "Microphone access requires HTTPS or localhost."
                : !(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
                  ? "Voice input is not supported in this browser."
                  : "This browser does not support MediaRecorder.";
              return (
                <div className="glass-card p-3 mb-2 flex items-center gap-3" data-testid="voice-unavailable-banner">
                  <Info size={16} className="text-[#B8A088]" />
                  <span className="font-body text-xs text-[#8B7355]">
                    {specific} Open the app on <b>localhost</b> or an <b>HTTPS</b> URL in Chrome, Edge, Firefox, or Safari.
                  </span>
                </div>
              );
            }
            if (micPermission === "denied") {
              return (
                <div className="glass-card p-3 mb-2 flex items-center gap-3" data-testid="voice-unavailable-banner">
                  <Info size={16} className="text-red-500" />
                  <span className="flex-1 font-body text-xs text-[#8B7355]">
                    Microphone permission is blocked. Click the lock / site-info icon in the address bar → allow microphone → reload.
                  </span>
                </div>
              );
            }
            return null;
          })()}
          <div className="glass-card-strong p-2 flex items-center gap-2">
            {(() => {
              const configReady = voiceConfigStatus === "ok";
              const configLoading = voiceConfigStatus === "loading";
              const voiceAvailable = browserSupport.ok && configReady && micPermission !== "denied";
              const needsAttention = !voiceAvailable && !configLoading;
              const unavailableReason =
                configLoading ? "Preparing voice…" :
                !browserSupport.checked ? "Checking…" :
                !browserSupport.ok && !window.isSecureContext ? "Microphone access requires HTTPS or localhost" :
                !browserSupport.ok ? "Voice input is not supported in this browser" :
                voiceConfigStatus === "disabled" ? "Voice services are temporarily unavailable" :
                voiceConfigStatus === "error" ? "Voice service unreachable — tap to retry" :
                micPermission === "denied" ? "Microphone permission is blocked — tap for instructions" :
                (recording ? "Stop recording" : "Tap to talk");
              // NOTE: button is NEVER visually "disabled" — startRecording/stopRecording
              // handles each failure case with a clear toast. Only hard-block while mid-send.
              const hardBlock = processing || loading;
              return (
                <button
                  onClick={recording ? stopRecording : startRecording}
                  disabled={hardBlock}
                  data-testid="voice-btn"
                  aria-label={unavailableReason}
                  title={unavailableReason}
                  className={`p-2.5 rounded-full transition-all flex items-center justify-center relative ${
                    recording
                      ? "bg-red-500 text-white shadow-md"
                      : voiceAvailable
                        ? "bg-[#C4A265] text-white hover:bg-[#B8965C] shadow-sm"
                        : needsAttention
                          ? "bg-amber-100 text-amber-700 hover:bg-amber-200 ring-1 ring-amber-300"
                          : "bg-[#EDE6DC] text-[#B8A088]"
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  {configLoading
                    ? <SpinnerGap size={18} className="animate-spin" />
                    : recording
                      ? <MicrophoneSlash size={18} weight="fill" />
                      : <Microphone size={18} weight={voiceAvailable || needsAttention ? "fill" : "duotone"} />}
                  {needsAttention && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-500 rounded-full ring-2 ring-white" />
                  )}
                </button>
              );
            })()}
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }}}
              placeholder={recording ? "Listening…" : "Speak or write your heart"} data-testid="chat-input"
              disabled={recording || processing}
              className="flex-1 bg-transparent text-[#2D1B0E] text-sm font-body placeholder:text-[#B8A088] focus:outline-none py-2 disabled:opacity-50" />
            <button onClick={() => sendMessage()} disabled={!input.trim() || loading || recording || processing} data-testid="chat-send-btn"
              className="px-5 py-2 rounded-full bg-[#EDE6DC] text-[#5C4033] text-sm font-body font-medium hover:bg-[#E0D5C5] transition-all disabled:opacity-40">
              {loading ? <SpinnerGap size={16} className="animate-spin" /> : "Send"}
            </button>
          </div>
          <div className="flex items-center justify-between mt-1.5 text-[#B8A088] text-[9px] font-body">
            <span>AI-assisted spiritual guidance. Not a replacement for pastoral counsel.</span>
            {voiceConfigStatus === "ok" && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoplay}
                    onChange={(e) => setAutoplay(e.target.checked)}
                    data-testid="voice-autoplay-toggle"
                    className="accent-[#C4A265]"
                  />
                  autoplay
                </label>
                <select
                  value={voiceId}
                  onChange={(e) => setVoiceId(e.target.value)}
                  data-testid="voice-select"
                  className="bg-transparent text-[#8B7355] text-[10px] font-body focus:outline-none"
                >
                  {voiceConfig.voices?.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
